import { useEffect } from 'react';
import { Box } from '@mantine/core';
import { WizardShell } from '../components/WizardShell';

type BookCallScreenProps = {
  calendlyUrl: string;
  firstName: string;
  lastName: string;
  email: string;
  onBooked: () => void;
  onSkip: () => void;
};

// Shrinks to fit the viewport so "Skip for now" stays visible without scrolling, but
// never below the height Calendly needs before its own layout starts scrolling.
const EMBED_HEIGHT = 'clamp(520px, calc(100vh - 280px), 700px)';
// Calendly's own snippet sets this; below it the scheduler's layout breaks.
const EMBED_MIN_WIDTH = 320;
const STEP_MAX_WIDTH = 760;
const EVENT_SCHEDULED = 'calendly.event_scheduled';

/**
 * Scheduling step, shown after the account exists but before the "check your inbox"
 * confirmation so that the email instruction stays the last thing the prospect sees.
 *
 * Uses Calendly's iframe embed rather than its `widget.js` snippet: the script version
 * needs a node injected outside React's tree and torn down on unmount, and everything
 * this step needs - prefill, display options, and the booking event - works without it.
 */
export function BookCallScreen({
  calendlyUrl,
  firstName,
  lastName,
  email,
  onBooked,
  onSkip,
}: BookCallScreenProps) {
  useCalendlyBookingListener(calendlyUrl, onBooked);

  return (
    <WizardShell
      title="Pick a time for your setup session"
      subtitle="Choose a slot that suits you - you can rearrange it later if you need to."
      maxWidth={STEP_MAX_WIDTH}
      compact
      onBack={onSkip}
      backLabel="Skip for now"
    >
      <Box mx={-24} my={-24}>
        <iframe
          src={buildCalendlyUrl(calendlyUrl, firstName, lastName, email)}
          title="Book your setup session"
          width="100%"
          style={{
            border: 0,
            display: 'block',
            minWidth: EMBED_MIN_WIDTH,
            height: EMBED_HEIGHT,
          }}
        />
      </Box>
    </WizardShell>
  );
}

/**
 * Advances past this step once Calendly reports a booking, so a prospect who books is
 * not left looking at a scheduler whose only remaining control says "skip".
 */
function useCalendlyBookingListener(calendlyUrl: string, onBooked: () => void) {
  useEffect(() => {
    const calendlyOrigin = new URL(calendlyUrl).origin;

    const handleMessage = (event: MessageEvent) => {
      // Any page can postMessage to this window, so the origin is checked before the
      // payload is trusted.
      if (event.origin !== calendlyOrigin) return;
      if (event.data?.event === EVENT_SCHEDULED) onBooked();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [calendlyUrl, onBooked]);
}

export function buildCalendlyUrl(
  calendlyUrl: string,
  firstName: string,
  lastName: string,
  email: string,
): string {
  const url = new URL(calendlyUrl);
  url.searchParams.set('name', `${firstName} ${lastName}`.trim());
  url.searchParams.set('email', email);
  // Consent is covered by the privacy policy linked from the review step, so Calendly's
  // own banner would be a second, redundant notice.
  url.searchParams.set('hide_gdpr_banner', '1');
  return url.toString();
}
